import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ScriptEdit } from '@/types/rebuild';
import {
  highlightSpansForEdits,
  segmentTextWithHighlights,
} from '@/utils/rebuildText';

interface RebuildScriptViewerProps {
  currentText: string;
  edits: ScriptEdit[];
  onChange: (text: string) => void;
  onSave: (text: string) => void;
  highlightEditId?: string | null;
}

export function RebuildScriptViewer({
  currentText,
  edits,
  onChange,
  onSave,
  highlightEditId,
}: RebuildScriptViewerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const spans = useMemo(
    () => highlightSpansForEdits(currentText, edits),
    [currentText, edits],
  );

  const previewSegments = useMemo(
    () => segmentTextWithHighlights(currentText, spans),
    [currentText, spans],
  );

  const debouncedSave = useCallback(
    (text: string) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => onSave(text), 1200);
    },
    [onSave],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!highlightEditId || !textareaRef.current) return;
    const span = spans.find((s) => s.editId === highlightEditId);
    if (span) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(span.start, span.end);
      textareaRef.current.scrollTop =
        (span.start / Math.max(currentText.length, 1)) * textareaRef.current.scrollHeight;
    }
  }, [highlightEditId, spans, currentText.length]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 mb-2 px-1">
        <p className="text-[10px] uppercase tracking-wider text-white/35 font-sans">
          Preview — highlighted regions show applied AI edits
        </p>
        <div
          className="mt-2 max-h-32 overflow-y-auto rounded border border-[#2a2a2a] bg-[#0a0a0a] p-3 font-mono text-xs text-white/60 whitespace-pre-wrap leading-relaxed"
          aria-hidden
        >
          {previewSegments.map((seg, i) =>
            seg.highlight ? (
              <mark
                key={i}
                className="bg-amber-500/20 text-amber-100/90 rounded-sm px-0.5"
                data-edit-id={seg.editId}
              >
                {seg.text}
              </mark>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </div>
      </div>
      <textarea
        ref={textareaRef}
        value={currentText}
        onChange={(e) => {
          onChange(e.target.value);
          debouncedSave(e.target.value);
        }}
        className="flex-1 min-h-[420px] w-full resize-y rounded-lg border border-[#2a2a2a] bg-[#0d0d0d] p-4 font-mono text-sm text-white/85 leading-relaxed focus:outline-none focus:ring-1 focus:ring-amber-500/40"
        spellCheck={false}
      />
    </div>
  );
}
